import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Job } from '../models/Job';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';

export function useJobs(contractId?: string | null, organizationId?: string | null) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    setLoading(true);
    
    let q;
    let needsSorting = false;
    if (contractId) {
      // Filter by specific contract - remove orderBy to avoid index requirement
      q = query(
        collection(db, 'jobs'),
        where('contractId', '==', contractId)
      );
      needsSorting = true;
    } else if (organizationId) {
      // Filter by organizationId - remove orderBy to avoid index requirement
      q = query(
        collection(db, 'jobs'),
        where('organizationId', '==', organizationId)
      );
      needsSorting = true;
    } else {
      // No filter - query all jobs (SUPER_ADMIN can see all)
      if (currentUser?.role === UserRole.SUPER_ADMIN) {
        q = query(
          collection(db, 'jobs'),
          orderBy('createdAt', 'desc')
        );
        // No sorting needed - Firestore handles it
      } else {
        setJobs([]);
        setLoading(false);
        return;
      }
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const jobsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate(),
            startDate: data.startDate?.toDate(),
            completedDate: data.completedDate?.toDate(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Job;
        });
        
        // Sort by createdAt descending (newest first) if we removed orderBy
        if (needsSorting) {
          jobsData.sort((a, b) => {
            const aTime = a.createdAt.getTime();
            const bTime = b.createdAt.getTime();
            return bTime - aTime; // Descending order
          });
        }
        
        setJobs(jobsData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        // Check if it's an index building error
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
        } else {
          setError(err as Error);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contractId, organizationId, currentUser?.role]);

  // Helper function to convert Date objects and date strings to Timestamps recursively
  const convertDatesToTimestamps = (obj: any, dateFields: string[] = []): any => {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Date) return Timestamp.fromDate(obj);
    if (obj instanceof Timestamp) return obj;
    // Handle date strings (from HTML date inputs)
    if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}/.test(obj)) {
      return Timestamp.fromDate(new Date(obj));
    }
    if (Array.isArray(obj)) return obj.map(item => convertDatesToTimestamps(item, dateFields));
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          // Convert date fields (dueDate, startDate, completedDate) from strings to Timestamps
          if (['dueDate', 'startDate', 'completedDate'].includes(key) && obj[key]) {
            if (typeof obj[key] === 'string') {
              converted[key] = Timestamp.fromDate(new Date(obj[key]));
            } else {
              converted[key] = convertDatesToTimestamps(obj[key], dateFields);
            }
          } else {
            converted[key] = convertDatesToTimestamps(obj[key], dateFields);
          }
        }
      }
      return converted;
    }
    return obj;
  };

  const createJob = async (jobData: Omit<Job, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = Timestamp.now();
      // Convert any Date objects or date strings to Timestamps
      const convertedData = convertDatesToTimestamps(jobData, ['dueDate', 'startDate', 'completedDate']);
      await addDoc(collection(db, 'jobs'), {
        ...convertedData,
        createdAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('Error creating job:', err);
      throw err;
    }
  };

  const updateJob = async (jobId: string, updates: Partial<Job>) => {
    try {
      const jobRef = doc(db, 'jobs', jobId);
      await updateDoc(jobRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating job:', err);
      throw err;
    }
  };

  const deleteJob = async (jobId: string) => {
    try {
      const jobRef = doc(db, 'jobs', jobId);
      await deleteDoc(jobRef);
    } catch (err) {
      console.error('Error deleting job:', err);
      throw err;
    }
  };

  return {
    jobs,
    loading,
    error,
    createJob,
    updateJob,
    deleteJob,
  };
}
